import React, { useRef, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { AppLayout } from "@/components/AppLayout";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import Dashboard from "@/pages/Dashboard";
import Mentor from "@/pages/Mentor";
import Knowledge from "@/pages/Knowledge";
import KnowledgeCategory from "@/pages/KnowledgeCategory";
import Dictionary from "@/pages/Dictionary";
import Translator from "@/pages/Translator";
import DealBuilder from "@/pages/DealBuilder";
import Negotiation from "@/pages/Negotiation";
import University from "@/pages/University";
import PathDetail from "@/pages/PathDetail";
import CaseStudies from "@/pages/CaseStudies";
import CaseStudyDetail from "@/pages/CaseStudyDetail";
import IQTest from "@/pages/IQTest";
import IQShare from "@/pages/IQShare";
import Resources from "@/pages/Resources";
import Pricing from "@/pages/Pricing";
import PaymentSuccess from "@/pages/PaymentSuccess";
import Account from "@/pages/Account";
import SearchResults from "@/pages/SearchResults";
import ForgotPassword from "@/pages/ForgotPassword";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import DecisionLab from "@/pages/DecisionLab";
import Frameworks from "@/pages/Frameworks";
import FounderRoadmap from "@/pages/FounderRoadmap";
import DealAnalyzer from "@/pages/DealAnalyzer";
import Challenges from "@/pages/Challenges";
import ChallengeDetail from "@/pages/ChallengeDetail";

function FullScreenLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#050505]">
      <div className="flex flex-col items-center gap-4">
        <div className="h-10 w-10 rounded-full border-2 border-white/10 border-t-[#0055FF] animate-spin" />
        <p className="font-mono-eco text-xs uppercase tracking-widest text-slate-500">Loading ECO</p>
      </div>
    </div>
  );
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const processed = useRef(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const hash = window.location.hash || "";
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    (async () => {
      try {
        const { data } = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
        setUser({ ...data.user, is_pro: data.user.tier === "pro" });
        window.history.replaceState(null, "", "/dashboard");
        await checkAuth();
        navigate("/dashboard", { replace: true });
      } catch (e) {
        navigate("/login", { replace: true });
      }
    })();
  }, [navigate, setUser, checkAuth]);

  return <FullScreenLoader />;
}

function ProtectedRoute({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user === false) return <Navigate to="/login" replace />;
  return <AppLayout>{children}</AppLayout>;
}

function PublicOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <FullScreenLoader />;
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<PublicOnly><Auth /></PublicOnly>} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/iq/r/:shareId" element={<IQShare />} />
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/mentor" element={<ProtectedRoute><Mentor /></ProtectedRoute>} />
      <Route path="/knowledge" element={<ProtectedRoute><Knowledge /></ProtectedRoute>} />
      <Route path="/knowledge/:categoryId" element={<ProtectedRoute><KnowledgeCategory /></ProtectedRoute>} />
      <Route path="/dictionary" element={<ProtectedRoute><Dictionary /></ProtectedRoute>} />
      <Route path="/translator" element={<ProtectedRoute><Translator /></ProtectedRoute>} />
      <Route path="/deal-builder" element={<ProtectedRoute><DealBuilder /></ProtectedRoute>} />
      <Route path="/negotiation" element={<ProtectedRoute><Negotiation /></ProtectedRoute>} />
      <Route path="/decision-lab" element={<ProtectedRoute><DecisionLab /></ProtectedRoute>} />
      <Route path="/deal-analyzer" element={<ProtectedRoute><DealAnalyzer /></ProtectedRoute>} />
      <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
      <Route path="/challenges/:cid" element={<ProtectedRoute><ChallengeDetail /></ProtectedRoute>} />
      <Route path="/frameworks" element={<ProtectedRoute><Frameworks /></ProtectedRoute>} />
      <Route path="/roadmap" element={<ProtectedRoute><FounderRoadmap /></ProtectedRoute>} />
      <Route path="/university" element={<ProtectedRoute><University /></ProtectedRoute>} />
      <Route path="/university/:pathId" element={<ProtectedRoute><PathDetail /></ProtectedRoute>} />
      <Route path="/case-studies" element={<ProtectedRoute><CaseStudies /></ProtectedRoute>} />
      <Route path="/case-studies/:studyId" element={<ProtectedRoute><CaseStudyDetail /></ProtectedRoute>} />
      <Route path="/iq-test" element={<ProtectedRoute><IQTest /></ProtectedRoute>} />
      <Route path="/resources" element={<ProtectedRoute><Resources /></ProtectedRoute>} />
      <Route path="/search" element={<ProtectedRoute><SearchResults /></ProtectedRoute>} />
      <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster theme="dark" position="top-center" richColors closeButton />
      </AuthProvider>
    </BrowserRouter>
  );
}
